import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useNavigate, useSearchParams } from "react-router"
import { Plus } from "@phosphor-icons/react"
import { isEqual } from "es-toolkit/predicate"
import { useUser } from "@/apis/bff"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { getInsensitiveSearchParams } from "@/lib/get-insensitive-search-params"
import { scrollIntoField } from "@/lib/scrollToId"
import { cn } from "@/lib/utils"
import { EpisodeOfCareActions } from "@/modules/patients/detail/episode-of-care/components/episode-of-care-actions"
import { EpisodeOfCareRelatedConditions } from "@/modules/patients/detail/episode-of-care/components/related-conditions"
import { EpisodeOfCareNavigationDropdown } from "@/modules/patients/detail/episode-of-care/episode-of-care-nav-dropdown"
import {
  createProgressNoteHandler,
  validateProgressNoteCreatable,
} from "@/modules/patients/detail/episode-of-care/lib/create-progress-note"
import { getEpisodeOfCareProgressNotesConnectionId } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-connection"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { AdministrativeProgressNoteCard } from "@/modules/patients/detail/progress-note/administrative-progress-note-card"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { getIsProgressNoteEditable } from "@/modules/patients/detail/progress-note/lib/get-is-progress-note-editable"
import { getProgressNoteKind } from "@/modules/patients/detail/progress-note/lib/get-progress-note-kind"
import { MyNotesFilter } from "@/modules/patients/detail/progress-note/my-notes-filter"
import { ProgressNoteCard } from "@/modules/patients/detail/progress-note/progress-note-card"
import { ProgressNotesFilter } from "@/modules/patients/detail/progress-notes-filter"
import { useDocumentStore } from "@/modules/patients/detail/store/useDocumentStore"
import { useEditEpisodeOfCareDialogStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareDialogStore"
import {
  useAllProgressNotesFilters,
  useEpisodeOfCareStoreContext,
} from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useIsInContextOfAppointment } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useProgressNoteData } from "@/modules/patients/hooks/useProgressNoteData"
import { hasTreatmentPlanPdf } from "@/modules/patients/libs/has-treatment-plan-pdf"
import type { PatientContextAppointmentFragment$data } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import type { chPatientEpisodesOfCareQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientEpisodesOfCareQuery.graphql"
import { createProgressNote } from "@/modules/patients/services/createProgressNote"
import type { dePatientEpisodesOfCareQuery$data } from "@/modules/patients/services/de/__generated__/dePatientEpisodesOfCareQuery.graphql"
import {
  CH_PROGRESS_NOTE_DOCUMENT_TYPES,
  DE_PROGRESS_NOTE_DOCUMENT_TYPES,
} from "@/modules/patients/types/ProgressNoteDocument"
import type { Country } from "@/types/__generated__/union-types"

interface ProgressNotesFeedProps {
  country: Country
  patientId: string
  patientEpisodesOfCareRef: dePatientEpisodesOfCareQuery$data & chPatientEpisodesOfCareQuery$data
  appointmentId: string | null
  isEditMode: boolean
}

interface ProgressNoteTitleProps extends ProgressNotesFeedProps {
  isCreatable: boolean
  isInContextOfAppointment: boolean
  contextAppointment: PatientContextAppointmentFragment$data | null | undefined
  onCreate: () => void
}
const ProgressNoteTitle = (props: ProgressNoteTitleProps) => {
  const {
    appointmentId,
    contextAppointment,
    isCreatable,
    isEditMode,
    isInContextOfAppointment,
    patientEpisodesOfCareRef,
    patientId,
    onCreate,
  } = props
  const { t } = useTranslation()
  const episodeOfCareFragmentReference = useEpisodeOfCareStoreContext((state) => state.episodeOfCareFragmentReference)

  if (episodeOfCareFragmentReference) {
    return null
  }

  return (
    <div className="mb-2 flex">
      <EpisodeOfCareNavigationDropdown
        patientEpisodesOfCareRef={patientEpisodesOfCareRef}
        appointmentId={appointmentId}
        patientId={patientId}
        isEditMode={isEditMode}
      />
      <div className="flex w-full justify-between">
        <p className="text-on-surface pt-2 text-xl font-bold" data-testid="all-progress-note-title">
          {t("episodeOfCare.allProgressNotes")}
        </p>
        {isEditMode && isInContextOfAppointment && contextAppointment?.isCompleted === false && (
          <Button
            variant="circle"
            className="h-10 w-10"
            data-testid="new-draft-progress-note-btn"
            disabled={!isCreatable}
            onClick={isCreatable ? onCreate : undefined}>
            <Plus className="text-on-surface-subdued" />
          </Button>
        )}
      </div>
    </div>
  )
}

const ProgressNotesFeed = (props: ProgressNotesFeedProps) => {
  const { country, patientId, appointmentId, patientEpisodesOfCareRef, isEditMode } = props
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()
  const episodeOfCareFragmentReference = useEpisodeOfCareStoreContext((state) => state.episodeOfCareFragmentReference)
  const progressNotesDataStore = useEpisodeOfCareStoreContext((state) => state.progressNoteAndParentData)
  const setProgressNotesData = useEpisodeOfCareStoreContext((state) => state.setProgressNotesData)
  const resetAllProgressNotesFilters = useEpisodeOfCareStoreContext((state) => state.resetAllProgressNotesFilters)
  const initializeProgressNoteFilters = useEpisodeOfCareStoreContext((state) => state.initializeProgressNoteFilters)
  const progressNotesFilters = useEpisodeOfCareStoreContext((state) => state.progressNotesFilters)

  const { hasFilters, filters, hasDocumentFilters } = useAllProgressNotesFilters()
  const { onlyMyNotes } = filters

  const progressNoteForms = useProgressNoteFormContext((state) => state.progressNoteForms)
  const initializeProgressNoteDocuments = useDocumentStore((state) => state.actions.initializeProgressNoteDocuments)
  const { userName } = useUser()

  const selectedEpisodeOfCare = useFragment(getEpisodeOfCareFragment(country), episodeOfCareFragmentReference)

  const progressNoteData = useProgressNoteData(patientEpisodesOfCareRef, isEditMode)

  const { progressNoteAndParentData } = progressNoteData

  useEffect(() => {
    if (!isEqual(progressNotesDataStore, progressNoteAndParentData)) {
      setProgressNotesData(progressNoteAndParentData)
    }
  }, [progressNoteAndParentData, progressNotesDataStore, setProgressNotesData])

  useEffect(() => {
    initializeProgressNoteDocuments(progressNoteAndParentData, progressNoteForms, country)
  }, [country, initializeProgressNoteDocuments, progressNoteForms, progressNoteAndParentData])

  useEffect(() => {
    initializeProgressNoteFilters(progressNoteAndParentData)
  }, [progressNoteAndParentData, initializeProgressNoteFilters])

  useEffect(() => {
    const progressNoteParam = getInsensitiveSearchParams(searchParams, "progressNoteIdToScrollTo")
    if (progressNoteParam.value) {
      scrollIntoField(progressNoteParam.value)
      searchParams.delete(progressNoteParam.key)
      navigate({ search: searchParams.toString() }, { replace: true })
    }
  }, [navigate, searchParams])

  const isInContextOfAppointment = useIsInContextOfAppointment()
  const contextAppointment = useContextAppointment()
  const countryCode = useCountryParam()

  const getProgressNotes = () => {
    if (!selectedEpisodeOfCare) {
      return progressNoteAndParentData
        .filter((progressNoteData) => {
          const onlyMyNotesCondition =
            !onlyMyNotes || progressNoteData.episodeOfCareProgressNoteData.updatedBy === userName

          let activeDocumentTypeCondition = false

          const { episodeOfCareProgressNoteData: progressNote } = progressNoteData

          switch (progressNote.__typename) {
            case "ch_ProgressNote":
              activeDocumentTypeCondition = CH_PROGRESS_NOTE_DOCUMENT_TYPES.some((type) => {
                if (type === "carePlanSummary") {
                  return filters[type] && hasTreatmentPlanPdf(progressNote)
                } else if (type === "medicalReportAttachments") {
                  return filters[type] && progressNote[type]?.edges?.at(0)
                }
                return filters[type] && progressNote[type]
              })
              break
            case "de_ProgressNote":
              activeDocumentTypeCondition = DE_PROGRESS_NOTE_DOCUMENT_TYPES.some((type) => {
                if (type === "carePlanSummary") {
                  return filters[type] && hasTreatmentPlanPdf(progressNote)
                } else if (type === "medicalReportAttachments") {
                  return filters[type] && progressNote[type]?.edges?.at(0)
                }
                return filters[type] && progressNote[type]
              })
              break
          }

          const documentTypeCondition = !hasDocumentFilters || activeDocumentTypeCondition

          return onlyMyNotesCondition && documentTypeCondition
        })
        .map((progressNoteData) => {
          const { kind } = progressNoteData.episodeOfCareProgressNoteData
          const Component = kind === "ADMINISTRATIVE" ? AdministrativeProgressNoteCard : ProgressNoteCard

          return (
            <Component
              appointmentContext={contextAppointment}
              country={country}
              key={progressNoteData.episodeOfCareProgressNoteData.id}
              isEditMode={isEditMode}
              parentEpisodeOfCare={progressNoteData.parentEpisodeOfCare}
              episodeOfCareProgressNoteData={progressNoteData.episodeOfCareProgressNoteData}
              patientId={patientId}
            />
          )
        })
    } else {
      return progressNoteAndParentData.map((progressNoteData) => {
        if (progressNoteData.episodeOfCareProgressNoteData.episodeOfCareId === selectedEpisodeOfCare.id) {
          const { kind } = progressNoteData.episodeOfCareProgressNoteData
          const Component = kind === "ADMINISTRATIVE" ? AdministrativeProgressNoteCard : ProgressNoteCard

          return (
            <Component
              appointmentContext={contextAppointment}
              country={country}
              key={progressNoteData.episodeOfCareProgressNoteData.id}
              isEditMode={isEditMode}
              parentEpisodeOfCare={progressNoteData.parentEpisodeOfCare}
              episodeOfCareProgressNoteData={progressNoteData.episodeOfCareProgressNoteData}
              patientId={patientId}
            />
          )
        }
      })
    }
  }

  const hasProgressNotes = progressNoteAndParentData.find(
    (progressNoteData) => progressNoteData.episodeOfCareProgressNoteData.episodeOfCareId === selectedEpisodeOfCare?.id,
  )

  const dataPatientEpisodesOfCareData = patientEpisodesOfCareRef.patientEpisodesOfCare?.edges?.map((e) => e.node)

  const isCreatable = validateProgressNoteCreatable({
    appointmentId,
    dataPatientEpisodesOfCareData,
    isInContextOfAppointment,
    progressNoteKind: contextAppointment ? getProgressNoteKind(contextAppointment.service) : null,
    userName,
  })

  const handleCreateProgressNote = () => {
    if (contextAppointment) {
      const progressNoteKind = getProgressNoteKind(contextAppointment.service)
      createProgressNoteHandler({
        appointmentId: contextAppointment.id,
        countryCode,
        dataPatientEpisodesOfCareData,
        patientId,
        progressNoteKind,
        language: contextAppointment.language,
      })
    }
  }

  if (progressNoteAndParentData.length) {
    return (
      <div className="animate-fade-in-bottom grid grow grid-rows-1" data-testid="progress-note-feed">
        <div className="grid grid-cols-1 grid-rows-1 justify-center">
          <ScrollWrapper className="h-full grid-rows-1">
            <div className="mx-auto w-full max-w-186 px-4 pt-8">
              <ProgressNoteTitle
                {...props}
                contextAppointment={contextAppointment}
                isCreatable={isCreatable}
                isInContextOfAppointment={isInContextOfAppointment}
                onCreate={handleCreateProgressNote}
              />
              {episodeOfCareFragmentReference && (
                <EpisodeOfCareHeader
                  episodeOfCareRef={episodeOfCareFragmentReference}
                  appointmentId={appointmentId}
                  country={country}
                  patientId={patientId}
                  isEditMode={isEditMode}
                  patientEpisodesOfCareRef={patientEpisodesOfCareRef}
                />
              )}
              {!episodeOfCareFragmentReference && (
                <div className="flex items-center gap-2 pb-4" data-testid="all-progress-notes-filter">
                  <MyNotesFilter data-testid="my-notes-filter" />
                  <ProgressNotesFilter data-testid="progress-notes-filter" documentTypes={progressNotesFilters} />
                  <Button
                    variant="secondary"
                    data-testid="appointment-clear-filter"
                    className={cn(
                      "text-primary border-none bg-transparent px-2 py-0",
                      !hasFilters && "text-on-surface-disabled pointer-events-none",
                    )}
                    onClick={resetAllProgressNotesFilters}>
                    {t("common.button.clearFilters")}
                  </Button>
                </div>
              )}
              {selectedEpisodeOfCare && !hasProgressNotes && isEditMode && (
                <Banner type="inline" variant="info">
                  {t("episodeOfCare.emptyEpisodeOfCareWarning")}
                </Banner>
              )}
              <div className="flex flex-col gap-4">{getProgressNotes()}</div>
            </div>
          </ScrollWrapper>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-186 shrink-0 basis-auto px-4 pt-8 @md/patient-layout:min-w-0 @lg/patient-layout:min-w-137.5">
      <ProgressNoteTitle
        {...props}
        contextAppointment={contextAppointment}
        isCreatable={isCreatable}
        isInContextOfAppointment={isInContextOfAppointment}
        onCreate={handleCreateProgressNote}
      />
      {episodeOfCareFragmentReference && (
        <EpisodeOfCareHeader
          episodeOfCareRef={episodeOfCareFragmentReference}
          appointmentId={appointmentId}
          country={country}
          patientId={patientId}
          isEditMode={isEditMode}
          patientEpisodesOfCareRef={patientEpisodesOfCareRef}
        />
      )}
      <div className="grid h-full w-full grid-rows-1">
        <div className="text-on-surface grid content-center gap-2 p-4 text-center">
          <h2 className="text-lg font-bold">{t("episodeOfCare.emptyPlaceholder.title")}</h2>
          <span className="text-on-surface-subdued">{t("episodeOfCare.emptyPlaceholder.description")}</span>
        </div>
      </div>
    </div>
  )
}

type EpisodeOfCareHeaderProps = {
  episodeOfCareRef: EpisodeOfCareFragment$key
  patientEpisodesOfCareRef: dePatientEpisodesOfCareQuery$data & chPatientEpisodesOfCareQuery$data
  appointmentId: string | null
  country: Country
  patientId: string
  isEditMode: boolean
}

const EpisodeOfCareHeader = (props: EpisodeOfCareHeaderProps) => {
  const { episodeOfCareRef, appointmentId, country, patientId, isEditMode, patientEpisodesOfCareRef } = props
  const { t } = useTranslation()

  const {
    progressNotes,
    icdDiagnosis: { text },
    id: episodeOfCareId,
    isUpdatable: isEpisodeOfCareUpdatable,
  } = useFragment(getEpisodeOfCareFragment(country), episodeOfCareRef)
  const { userName } = useUser()

  const contextAppointment = useContextAppointment()

  const createProgressNoteHandler = () => {
    if (contextAppointment) {
      const { id: appointmentId, language, service } = contextAppointment
      const connectionId = getEpisodeOfCareProgressNotesConnectionId(episodeOfCareId)
      const kind = getProgressNoteKind(service)

      createProgressNote(country, { appointmentId, patientId, episodeOfCareId, kind, language }, [connectionId])
    }
  }

  const isInContextOfAppointment = useIsInContextOfAppointment()

  const cannotAddProgressNote = progressNotes?.edges?.some(({ node }) => {
    const progressNote = node
    return getIsProgressNoteEditable(progressNote, userName, appointmentId, isInContextOfAppointment)
  })

  const setEpisodeOfCareRef = useEditEpisodeOfCareDialogStoreContext((state) => state.setEpisodeOfCareRef)
  const progressNoteKind = contextAppointment ? getProgressNoteKind(contextAppointment.service) : null
  return (
    <div className="mb-4 flex flex-col gap-4 self-stretch">
      <div className="grid gap-2">
        <div className="flex grow items-center">
          <div className="@xxl:hidden @xs:flex">
            <EpisodeOfCareNavigationDropdown
              patientEpisodesOfCareRef={patientEpisodesOfCareRef}
              appointmentId={appointmentId}
              patientId={patientId}
              isEditMode={isEditMode}
            />
          </div>
          <h2 className="text-on-surface text-xl font-bold" data-testid="episode-of-care-description">
            {text}
          </h2>
          {isEditMode &&
            isEpisodeOfCareUpdatable &&
            progressNoteKind === "MEDICAL" &&
            contextAppointment?.isCompleted === false && (
              <EpisodeOfCareActions
                onEdit={() => setEpisodeOfCareRef(episodeOfCareRef)}
                disabled={cannotAddProgressNote}
                onCreateProgressNote={createProgressNoteHandler}
              />
            )}
        </div>
        <EpisodeOfCareRelatedConditions country={country} episodeOfCareRef={episodeOfCareRef} />
      </div>
      {!isEpisodeOfCareUpdatable && (
        <Banner type="inline" variant="info">
          {t("episodeOfCare.updatedDateOverDue")}
        </Banner>
      )}
    </div>
  )
}

export { ProgressNotesFeed }
